// Team color mappings for major professional and collegiate sports leagues (NFL, NBA, MLB, NHL, NCAA)

/** Team primary and secondary color configuration */
export interface TeamColors {
  primary: string;
  secondary: string;
}

// NFL Team Colors
export const NFL_TEAM_COLORS: Record<string, TeamColors> = {
  'Arizona Cardinals': { primary: '#97233F', secondary: '#000000' },
  'Atlanta Falcons': { primary: '#A71930', secondary: '#000000' },
  'Baltimore Ravens': { primary: '#241773', secondary: '#9E7C0C' },
  'Buffalo Bills': { primary: '#00338D', secondary: '#C60C30' },
  'Carolina Panthers': { primary: '#0085CA', secondary: '#101820' },
  'Chicago Bears': { primary: '#0B162A', secondary: '#C83803' },
  'Cincinnati Bengals': { primary: '#FB4F14', secondary: '#000000' },
  'Cleveland Browns': { primary: '#311D00', secondary: '#FF3C00' },
  'Dallas Cowboys': { primary: '#003594', secondary: '#869397' },
  'Denver Broncos': { primary: '#FB4F14', secondary: '#002244' },
  'Detroit Lions': { primary: '#0076B6', secondary: '#B0B7BC' },
  'Green Bay Packers': { primary: '#203731', secondary: '#FFB612' },
  'Houston Texans': { primary: '#03202F', secondary: '#A71930' },
  'Indianapolis Colts': { primary: '#002C5F', secondary: '#A2AAAD' },
  'Jacksonville Jaguars': { primary: '#006778', secondary: '#9F792C' },
  'Kansas City Chiefs': { primary: '#E31837', secondary: '#FFB81C' },
  'Las Vegas Raiders': { primary: '#000000', secondary: '#A5ACAF' },
  'Los Angeles Chargers': { primary: '#0080C6', secondary: '#FFC20E' },
  'Los Angeles Rams': { primary: '#003594', secondary: '#FFA300' },
  'Miami Dolphins': { primary: '#008E97', secondary: '#FC4C02' },
  'Minnesota Vikings': { primary: '#4F2683', secondary: '#FFC62F' },
  'New England Patriots': { primary: '#002244', secondary: '#C60C30' },
  'New Orleans Saints': { primary: '#D3BC8D', secondary: '#101820' },
  'New York Giants': { primary: '#0B2265', secondary: '#A71930' },
  'New York Jets': { primary: '#125740', secondary: '#000000' },
  'Philadelphia Eagles': { primary: '#004C54', secondary: '#A5ACAF' },
  'Pittsburgh Steelers': { primary: '#FFB612', secondary: '#101820' },
  'San Francisco 49ers': { primary: '#AA0000', secondary: '#B3995D' },
  'Seattle Seahawks': { primary: '#002244', secondary: '#69BE28' },
  'Tampa Bay Buccaneers': { primary: '#D50A0A', secondary: '#FF7900' },
  'Tennessee Titans': { primary: '#0C2340', secondary: '#4B92DB' },
  'Washington Commanders': { primary: '#5A1414', secondary: '#FFB612' },
};

// NBA Team Colors
export const NBA_TEAM_COLORS: Record<string, TeamColors> = {
  'Atlanta Hawks': { primary: '#E03A3E', secondary: '#C1D32F' },
  'Boston Celtics': { primary: '#007A33', secondary: '#BA9653' },
  'Brooklyn Nets': { primary: '#000000', secondary: '#FFFFFF' },
  'Charlotte Hornets': { primary: '#1D1160', secondary: '#00788C' },
  'Chicago Bulls': { primary: '#CE1141', secondary: '#000000' },
  'Cleveland Cavaliers': { primary: '#860038', secondary: '#FDBB30' },
  'Dallas Mavericks': { primary: '#00538C', secondary: '#002B5E' },
  'Denver Nuggets': { primary: '#0E2240', secondary: '#FEC524' },
  'Detroit Pistons': { primary: '#C8102E', secondary: '#1D42BA' },
  'Golden State Warriors': { primary: '#1D428A', secondary: '#FFC72C' },
  'Houston Rockets': { primary: '#CE1141', secondary: '#000000' },
  'Indiana Pacers': { primary: '#002D62', secondary: '#FDBB30' },
  'LA Clippers': { primary: '#C8102E', secondary: '#1D428A' },
  'Los Angeles Clippers': { primary: '#C8102E', secondary: '#1D428A' },
  'Los Angeles Lakers': { primary: '#552583', secondary: '#FDB927' },
  'Memphis Grizzlies': { primary: '#5D76A9', secondary: '#12173F' },
  'Miami Heat': { primary: '#98002E', secondary: '#F9A01B' },
  'Milwaukee Bucks': { primary: '#00471B', secondary: '#EEE1C6' },
  'Minnesota Timberwolves': { primary: '#0C2340', secondary: '#236192' },
  'New Orleans Pelicans': { primary: '#0C2340', secondary: '#C8102E' },
  'New York Knicks': { primary: '#006BB6', secondary: '#F58426' },
  'Oklahoma City Thunder': { primary: '#007AC1', secondary: '#EF3B24' },
  'Orlando Magic': { primary: '#0077C0', secondary: '#C4CED4' },
  'Philadelphia 76ers': { primary: '#006BB6', secondary: '#ED174C' },
  'Phoenix Suns': { primary: '#1D1160', secondary: '#E56020' },
  'Portland Trail Blazers': { primary: '#E03A3E', secondary: '#000000' },
  'Sacramento Kings': { primary: '#5A2D81', secondary: '#63727A' },
  'San Antonio Spurs': { primary: '#C4CED4', secondary: '#000000' },
  'Toronto Raptors': { primary: '#CE1141', secondary: '#000000' },
  'Utah Jazz': { primary: '#002B5C', secondary: '#F9A01B' },
  'Washington Wizards': { primary: '#002B5C', secondary: '#E31837' },
};

// MLB Team Colors
export const MLB_TEAM_COLORS: Record<string, TeamColors> = {
  'Arizona Diamondbacks': { primary: '#A71930', secondary: '#E3D4AD' },
  'Atlanta Braves': { primary: '#CE1141', secondary: '#13274F' },
  'Baltimore Orioles': { primary: '#DF4601', secondary: '#000000' },
  'Boston Red Sox': { primary: '#BD3039', secondary: '#0C2340' },
  'Chicago Cubs': { primary: '#0E3386', secondary: '#CC3433' },
  'Chicago White Sox': { primary: '#27251F', secondary: '#C4CED4' },
  'Cincinnati Reds': { primary: '#C6011F', secondary: '#000000' },
  'Cleveland Guardians': { primary: '#00385D', secondary: '#E50022' },
  'Colorado Rockies': { primary: '#33006F', secondary: '#C4CED4' },
  'Detroit Tigers': { primary: '#0C2340', secondary: '#FA4616' },
  'Houston Astros': { primary: '#002D62', secondary: '#EB6E1F' },
  'Kansas City Royals': { primary: '#004687', secondary: '#BD9B60' },
  'Los Angeles Angels': { primary: '#BA0021', secondary: '#003263' },
  'Los Angeles Dodgers': { primary: '#005A9C', secondary: '#EF3E42' },
  'Miami Marlins': { primary: '#00A3E0', secondary: '#EF3340' },
  'Milwaukee Brewers': { primary: '#12284B', secondary: '#B6922E' },
  'Minnesota Twins': { primary: '#002B5C', secondary: '#D31145' },
  'New York Mets': { primary: '#002D72', secondary: '#FF5910' },
  'New York Yankees': { primary: '#003087', secondary: '#E4002C' },
  'Oakland Athletics': { primary: '#003831', secondary: '#EFB21E' },
  'Philadelphia Phillies': { primary: '#E81828', secondary: '#002D72' },
  'Pittsburgh Pirates': { primary: '#27251F', secondary: '#FDB827' },
  'San Diego Padres': { primary: '#2F241D', secondary: '#FFC425' },
  'San Francisco Giants': { primary: '#FD5A1E', secondary: '#27251F' },
  'Seattle Mariners': { primary: '#0C2C56', secondary: '#005C5C' },
  'St. Louis Cardinals': { primary: '#C41E3A', secondary: '#0C2340' },
  'Tampa Bay Rays': { primary: '#092C5C', secondary: '#8FBCE6' },
  'Texas Rangers': { primary: '#003278', secondary: '#C0111F' },
  'Toronto Blue Jays': { primary: '#134A8E', secondary: '#1D2D5C' },
  'Washington Nationals': { primary: '#AB0003', secondary: '#14225A' },
};

// NHL Team Colors
export const NHL_TEAM_COLORS: Record<string, TeamColors> = {
  'Anaheim Ducks': { primary: '#F47A38', secondary: '#B9975B' },
  'Arizona Coyotes': { primary: '#8C2633', secondary: '#E2D6B5' },
  'Boston Bruins': { primary: '#FFB81C', secondary: '#000000' },
  'Buffalo Sabres': { primary: '#002654', secondary: '#FCB514' },
  'Calgary Flames': { primary: '#C8102E', secondary: '#F1BE48' },
  'Carolina Hurricanes': { primary: '#CC0000', secondary: '#000000' },
  'Chicago Blackhawks': { primary: '#CF0A2C', secondary: '#FF671B' },
  'Colorado Avalanche': { primary: '#6F263D', secondary: '#236192' },
  'Columbus Blue Jackets': { primary: '#002654', secondary: '#CE1141' },
  'Dallas Stars': { primary: '#006847', secondary: '#8F8F8C' },
  'Detroit Red Wings': { primary: '#CE1126', secondary: '#FFFFFF' },
  'Edmonton Oilers': { primary: '#041E42', secondary: '#FF4C00' },
  'Florida Panthers': { primary: '#041E42', secondary: '#C8102E' },
  'Los Angeles Kings': { primary: '#111111', secondary: '#A2AAAD' },
  'Minnesota Wild': { primary: '#154734', secondary: '#A6192E' },
  'Montreal Canadiens': { primary: '#AF1E2D', secondary: '#192168' },
  'Nashville Predators': { primary: '#FFB81C', secondary: '#041E42' },
  'New Jersey Devils': { primary: '#CE1126', secondary: '#000000' },
  'New York Islanders': { primary: '#00539B', secondary: '#F47D30' },
  'New York Rangers': { primary: '#0038A8', secondary: '#CE1126' },
  'Ottawa Senators': { primary: '#C52032', secondary: '#C2912C' },
  'Philadelphia Flyers': { primary: '#F74902', secondary: '#000000' },
  'Pittsburgh Penguins': { primary: '#FCB514', secondary: '#000000' },
  'San Jose Sharks': { primary: '#006D75', secondary: '#EA7200' },
  'Seattle Kraken': { primary: '#001628', secondary: '#99D9D9' },
  'St. Louis Blues': { primary: '#002F87', secondary: '#FCB514' },
  'Tampa Bay Lightning': { primary: '#002868', secondary: '#FFFFFF' },
  'Toronto Maple Leafs': { primary: '#00205B', secondary: '#FFFFFF' },
  'Utah Hockey Club': { primary: '#69B3E7', secondary: '#010101' },
  'Vancouver Canucks': { primary: '#00205B', secondary: '#00843D' },
  'Vegas Golden Knights': { primary: '#B4975A', secondary: '#333F42' },
  'Washington Capitals': { primary: '#C8102E', secondary: '#041E42' },
  'Winnipeg Jets': { primary: '#041E42', secondary: '#004C97' },
};

// College Football Team Colors (Power 5 + Major Programs)
export const COLLEGE_TEAM_COLORS: Record<string, TeamColors> = {
  // SEC
  'Alabama Crimson Tide': { primary: '#9E1B32', secondary: '#828A8F' },
  'Arkansas Razorbacks': { primary: '#9D2235', secondary: '#FFFFFF' },
  'Auburn Tigers': { primary: '#0C2340', secondary: '#E87722' },
  'Florida Gators': { primary: '#0021A5', secondary: '#FA4616' },
  'Georgia Bulldogs': { primary: '#BA0C2F', secondary: '#000000' },
  'Kentucky Wildcats': { primary: '#0033A0', secondary: '#FFFFFF' },
  'LSU Tigers': { primary: '#461D7C', secondary: '#FDD023' },
  'Mississippi State Bulldogs': { primary: '#660000', secondary: '#FFFFFF' },
  'Missouri Tigers': { primary: '#F1B82D', secondary: '#000000' },
  'Ole Miss Rebels': { primary: '#CE1126', secondary: '#14213D' },
  'South Carolina Gamecocks': { primary: '#73000A', secondary: '#000000' },
  'Tennessee Volunteers': { primary: '#FF8200', secondary: '#FFFFFF' },
  'Texas A&M Aggies': { primary: '#500000', secondary: '#FFFFFF' },
  'Vanderbilt Commodores': { primary: '#866D4B', secondary: '#000000' },
  'Texas Longhorns': { primary: '#BF5700', secondary: '#FFFFFF' },
  'Oklahoma Sooners': { primary: '#841617', secondary: '#FDF9D8' },
  
  // Big Ten
  'Illinois Fighting Illini': { primary: '#13294B', secondary: '#E84A27' },
  'Indiana Hoosiers': { primary: '#990000', secondary: '#EEEDEB' },
  'Iowa Hawkeyes': { primary: '#FFCD00', secondary: '#000000' },
  'Maryland Terrapins': { primary: '#E03A3E', secondary: '#FFD520' },
  'Michigan Wolverines': { primary: '#00274C', secondary: '#FFCB05' },
  'Michigan State Spartans': { primary: '#18453B', secondary: '#FFFFFF' },
  'Minnesota Golden Gophers': { primary: '#7A0019', secondary: '#FFCC33' },
  'Nebraska Cornhuskers': { primary: '#E41C38', secondary: '#F5F1E7' },
  'Northwestern Wildcats': { primary: '#4E2A84', secondary: '#FFFFFF' },
  'Ohio State Buckeyes': { primary: '#BB0000', secondary: '#666666' },
  'Penn State Nittany Lions': { primary: '#041E42', secondary: '#FFFFFF' },
  'Purdue Boilermakers': { primary: '#CEB888', secondary: '#000000' },
  'Rutgers Scarlet Knights': { primary: '#CC0033', secondary: '#5F6A72' },
  'Wisconsin Badgers': { primary: '#C5050C', secondary: '#FFFFFF' },
  'UCLA Bruins': { primary: '#2D68C4', secondary: '#F2A900' },
  'USC Trojans': { primary: '#990000', secondary: '#FFC72C' },
  'Oregon Ducks': { primary: '#154733', secondary: '#FEE123' },
  'Washington Huskies': { primary: '#4B2E83', secondary: '#B7A57A' },
  
  // ACC
  'Boston College Eagles': { primary: '#98002E', secondary: '#BC9B6A' },
  'Clemson Tigers': { primary: '#F56600', secondary: '#522D80' },
  'Duke Blue Devils': { primary: '#003087', secondary: '#FFFFFF' },
  'Florida State Seminoles': { primary: '#782F40', secondary: '#CEB888' },
  'Georgia Tech Yellow Jackets': { primary: '#B3A369', secondary: '#003057' },
  'Louisville Cardinals': { primary: '#AD0000', secondary: '#000000' },
  'Miami Hurricanes': { primary: '#F47321', secondary: '#005030' },
  'NC State Wolfpack': { primary: '#CC0000', secondary: '#000000' },
  'North Carolina Tar Heels': { primary: '#7BAFD4', secondary: '#13294B' },
  'Pittsburgh Panthers': { primary: '#003594', secondary: '#FFB81C' },
  'Syracuse Orange': { primary: '#F76900', secondary: '#002D74' },
  'Virginia Cavaliers': { primary: '#232D4B', secondary: '#F84C1E' },
  'Virginia Tech Hokies': { primary: '#630031', secondary: '#CF4420' },
  'Wake Forest Demon Deacons': { primary: '#9E7E38', secondary: '#000000' },
  'Notre Dame Fighting Irish': { primary: '#0C2340', secondary: '#C99700' },
  'Stanford Cardinal': { primary: '#8C1515', secondary: '#FFFFFF' },
  'California Golden Bears': { primary: '#003262', secondary: '#FDB515' },
  'SMU Mustangs': { primary: '#0033A0', secondary: '#C8102E' },
  
  // Big 12
  'Baylor Bears': { primary: '#154734', secondary: '#FFB81C' },
  'BYU Cougars': { primary: '#002E5D', secondary: '#FFFFFF' },
  'Cincinnati Bearcats': { primary: '#E00122', secondary: '#000000' },
  'Houston Cougars': { primary: '#C8102E', secondary: '#FFFFFF' },
  'Iowa State Cyclones': { primary: '#C8102E', secondary: '#F1BE48' },
  'Kansas Jayhawks': { primary: '#0051BA', secondary: '#E8000D' },
  'Kansas State Wildcats': { primary: '#512888', secondary: '#FFFFFF' },
  'Oklahoma State Cowboys': { primary: '#FF7300', secondary: '#000000' },
  'TCU Horned Frogs': { primary: '#4D1979', secondary: '#A3A9AC' },
  'Texas Tech Red Raiders': { primary: '#CC0000', secondary: '#000000' },
  'UCF Knights': { primary: '#BA9B37', secondary: '#000000' },
  'West Virginia Mountaineers': { primary: '#002855', secondary: '#EAAA00' },
  'Arizona Wildcats': { primary: '#CC0033', secondary: '#003366' },
  'Arizona State Sun Devils': { primary: '#8C1D40', secondary: '#FFC627' },
  'Colorado Buffaloes': { primary: '#CFB87C', secondary: '#000000' },
  'Utah Utes': { primary: '#CC0000', secondary: '#FFFFFF' },
};

// Function to normalize team names for matching
const normalizeTeamName = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .trim();
};

// Get team colors by team name, with optional league hint
export const getTeamColors = (teamName: string, league?: string): TeamColors => {
  if (!teamName) {
    return { primary: '#6366f1', secondary: '#a5b4fc' }; // Default indigo
  }

  const normalizedName = normalizeTeamName(teamName);
  
  // Check specific league first if provided
  const leagueMaps: Record<string, Record<string, TeamColors>> = {
    'nfl': NFL_TEAM_COLORS,
    'nba': NBA_TEAM_COLORS,
    'mlb': MLB_TEAM_COLORS,
    'nhl': NHL_TEAM_COLORS,
    'ncaa': COLLEGE_TEAM_COLORS,
    'college': COLLEGE_TEAM_COLORS,
    'ncaaf': COLLEGE_TEAM_COLORS,
    'mens-college-basketball': COLLEGE_TEAM_COLORS,
    'womens-college-basketball': COLLEGE_TEAM_COLORS,
    'college-football': COLLEGE_TEAM_COLORS,
  };

  // If league is specified, check that league first
  if (league) {
    const normalizedLeague = league.toLowerCase();
    const leagueMap = leagueMaps[normalizedLeague];
    if (leagueMap) {
      for (const [key, colors] of Object.entries(leagueMap)) {
        if (normalizeTeamName(key).includes(normalizedName) || normalizedName.includes(normalizeTeamName(key))) {
          return colors;
        }
      }
    }
  }

  // Search all leagues
  const allMaps = [NFL_TEAM_COLORS, NBA_TEAM_COLORS, MLB_TEAM_COLORS, NHL_TEAM_COLORS, COLLEGE_TEAM_COLORS];
  
  for (const map of allMaps) {
    for (const [key, colors] of Object.entries(map)) {
      const normalizedKey = normalizeTeamName(key);
      // Check for exact match or partial match
      if (normalizedKey === normalizedName || 
          normalizedKey.includes(normalizedName) || 
          normalizedName.includes(normalizedKey)) {
        return colors;
      }
    }
  }

  // Generate a consistent color based on team name hash for unknown teams
  const hash = teamName.split('').reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);
  
  const hue = Math.abs(hash) % 360;
  return {
    primary: `hsl(${hue}, 70%, 45%)`,
    secondary: `hsl(${hue}, 60%, 65%)`,
  };
};

// Export a function to check if a team has official colors
export const hasOfficialTeamColors = (teamName: string): boolean => {
  const colors = getTeamColors(teamName);
  return !colors.primary.startsWith('hsl(');
};
